/**
 * /admin/$slug/templates/:key/:channel — tenant edits one template.
 *
 * Loads the global default + any existing override; lets the operator
 * save a tenant-scoped override or revert to the global. Wrapped in
 * PlanGate so non-large tenants see the editor disabled.
 */
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ArrowLeft, Save, Undo2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
} from '@bookaburra/ui';
import {
  VARIABLES_BY_KEY,
  type TemplateChannel,
  type TemplateKey,
} from '@bookaburra/notifications';
import { TemplateEditor } from '../components/TemplateEditor';
import { PlanGate } from '../components/PlanGate';
import { useDocumentTitle } from '../lib/use-document-title';
import { fetchBookingTenant } from '../booking-flow/api';

interface TenantTemplateDetail {
  key: string;
  channel: TemplateChannel;
  globalBody: string;
  override: { body: string; updatedAt: number; updatedBy: string | null } | null;
}

async function fetchTenantTemplate(
  slug: string,
  key: string,
  channel: string,
): Promise<TenantTemplateDetail> {
  const r = await fetch(
    `/api/admin/${encodeURIComponent(slug)}/templates/${encodeURIComponent(key)}/${encodeURIComponent(channel)}`,
  );
  if (!r.ok) throw new Error(`load_failed_${r.status}`);
  return r.json();
}

async function saveOverride(
  slug: string,
  key: string,
  channel: string,
  body: string,
): Promise<void> {
  const r = await fetch(
    `/api/admin/${encodeURIComponent(slug)}/templates/${encodeURIComponent(key)}/${encodeURIComponent(channel)}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ body }),
    },
  );
  if (!r.ok) {
    const j = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(j.error ?? `error ${r.status}`);
  }
}

async function clearOverride(
  slug: string,
  key: string,
  channel: string,
): Promise<void> {
  const r = await fetch(
    `/api/admin/${encodeURIComponent(slug)}/templates/${encodeURIComponent(key)}/${encodeURIComponent(channel)}`,
    { method: 'DELETE' },
  );
  if (!r.ok) throw new Error(`clear_failed_${r.status}`);
}

export const Route = createFileRoute('/admin/$slug/templates/$key/$channel')({
  component: TenantTemplateEditPage,
});

function TenantTemplateEditPage() {
  const { slug, key, channel } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const tenantBrand = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantBrand.data
      ? `Edit ${key} · ${tenantBrand.data.tenant.name} Admin`
      : 'Edit template',
  );

  const detailQ = useQuery({
    queryKey: ['admin-template', slug, key, channel],
    queryFn: () => fetchTenantTemplate(slug, key, channel),
  });

  const [body, setBody] = useState<string>('');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  useEffect(() => {
    if (!detailQ.data) return;
    setBody(detailQ.data.override?.body ?? detailQ.data.globalBody);
  }, [detailQ.data]);

  const saveMut = useMutation({
    mutationFn: () => saveOverride(slug, key, channel, body),
    onSuccess: () => {
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['admin-template', slug, key, channel] });
      qc.invalidateQueries({ queryKey: ['admin-templates', slug] });
    },
  });
  const clearMut = useMutation({
    mutationFn: () => clearOverride(slug, key, channel),
    onSuccess: () => {
      setSavedAt(null);
      qc.invalidateQueries({ queryKey: ['admin-template', slug, key, channel] });
      qc.invalidateQueries({ queryKey: ['admin-templates', slug] });
    },
  });

  const hasOverride = !!detailQ.data?.override;
  const dirty =
    detailQ.data &&
    body !== (detailQ.data.override?.body ?? detailQ.data.globalBody);

  const variables = VARIABLES_BY_KEY[key as TemplateKey] ?? [];

  return (
    <main className="mx-auto max-w-4xl px-6 py-10 sm:px-12">
      <Link
        to="/admin/$slug/templates"
        params={{ slug }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> All templates
      </Link>
      <header className="mt-2 mb-6">
        <h1 className="text-2xl font-bold tracking-tight">
          {key} <span className="text-muted-foreground">/</span> {channel}
        </h1>
        <p className="mt-1 text-xs text-muted-foreground">
          {hasOverride ? 'Currently using your override' : 'Currently using the Bookaburra default'}
        </p>
      </header>

      <PlanGate slug={slug} requiredPlan="large-business">
        {detailQ.isLoading ? (
          <div className="h-64 animate-pulse rounded-2xl bg-secondary" />
        ) : detailQ.error ? (
          <Alert variant="destructive">
            <AlertTitle>Couldn&rsquo;t load template</AlertTitle>
            <AlertDescription>{(detailQ.error as Error).message}</AlertDescription>
          </Alert>
        ) : detailQ.data ? (
          <Card className="p-5">
            <TemplateEditor
              templateKey={key}
              channel={channel as TemplateChannel}
              value={body}
              onChange={setBody}
              variables={variables}
            />
            {saveMut.error ? (
              <Alert variant="destructive" className="mt-4">
                <AlertTitle>Couldn&rsquo;t save</AlertTitle>
                <AlertDescription>{(saveMut.error as Error).message}</AlertDescription>
              </Alert>
            ) : null}
            {savedAt && !dirty ? (
              <Alert variant="success" className="mt-4">
                <AlertTitle>Saved</AlertTitle>
                <AlertDescription>
                  This template will now use your custom version.
                </AlertDescription>
              </Alert>
            ) : null}
            <div className="mt-5 flex items-center justify-between gap-3">
              {hasOverride ? (
                <Button
                  variant="ghost"
                  onClick={() => clearMut.mutate()}
                  loading={clearMut.isPending}
                  disabled={clearMut.isPending}
                >
                  <Undo2 className="h-4 w-4" /> Revert to default
                </Button>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate({ to: '/admin/$slug/templates', params: { slug } })}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => saveMut.mutate()}
                  loading={saveMut.isPending}
                  disabled={!dirty || saveMut.isPending}
                >
                  <Save className="h-4 w-4" /> Save override
                </Button>
              </div>
            </div>
          </Card>
        ) : null}
      </PlanGate>
    </main>
  );
}
